// Badge — small inline label with 7 semantic variants.
//
// success / warning / info were added on top of shadcn's default set so CRM
// can render deal stages and status chips without ad-hoc Tailwind classes
// (see docs/handoff/shared-packages/ui-primitives.md §3 "Badge").

import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from './utils/cn.js';

export const badgeVariants = cva(
  [
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
    'transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-muted text-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        success: 'border-transparent bg-[color:var(--color-success-500)] text-white',
        warning: 'border-transparent bg-[color:var(--color-warning-500)] text-[#4a2a00]',
        info: 'border-transparent bg-[color:var(--color-info-500)] text-white',
        outline: 'border-border bg-transparent text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps): ReactElement {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}
